import API from './api';

/**
 * Servicio para gestión de asignaciones entrenador-equipo
 * 
 * Maneja todas las operaciones CRUD para asignar entrenadores a equipos
 * en partidos organizados
 */

/**
 * Función auxiliar para manejar errores de API
 * @param {Error} error - Error de la API
 * @param {string} defaultMessage - Mensaje por defecto
 * @returns {Error} Error procesado
 */
const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || defaultMessage;
  const newError = new Error(message);
  newError.status = error.response?.status;
  return newError;
};

const entrenadorEnEquipoService = {
  /**
   * Asignar entrenador a un equipo
   * @param {Object} asignacionData - Datos de la asignación
   * @returns {Promise<Object>} Asignación creada
   */
  asignarEntrenadorAEquipo: async (asignacionData) => {
    try {
      const response = await API.post('/postgres/entrenador-equipo', asignacionData);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al asignar entrenador al equipo');
    }
  },

  /**
   * Obtener entrenadores asignados a un equipo
   * @param {number} equipoId - ID del equipo
   * @returns {Promise<Object>} Lista de entrenadores asignados
   */
  getEntrenadoresPorEquipo: async (equipoId) => {
    try {
      const response = await API.get(`/postgres/entrenador-equipo/equipo/${equipoId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al obtener entrenadores del equipo');
    }
  },

  /**
   * Obtener equipos asignados a un entrenador
   * @param {number} entrenadorId - ID del entrenador
   * @returns {Promise<Object>} Lista de equipos asignados
   */
  getEquiposPorEntrenador: async (entrenadorId) => {
    try {
      const response = await API.get(`/postgres/entrenador-equipo/entrenador/${entrenadorId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al obtener equipos del entrenador');
    }
  },

  /**
   * Remover entrenador de un equipo
   * @param {number} asignacionId - ID de la asignación
   * @returns {Promise<Object>} Confirmación de eliminación
   */
  removerEntrenadorDeEquipo: async (asignacionId) => {
    try {
      const response = await API.delete(`/postgres/entrenador-equipo/${asignacionId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al remover entrenador del equipo');
    }
  },

  /**
   * Obtener todas las asignaciones activas
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise<Object>} Lista de asignaciones con paginación
   */
  getTodasAsignaciones: async (filters = {}) => {
    try {
      const response = await API.get('/postgres/entrenador-equipo', { params: filters });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al obtener las asignaciones');
    }
  },

  /**
   * Obtener entrenadores disponibles para asignar
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise<Object>} Lista de entrenadores disponibles
   */
  getEntrenadoresDisponibles: async (filters = {}) => {
    try {
      const response = await API.get('/postgres/entrenadores', { params: filters });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al obtener entrenadores disponibles');
    }
  }
};

export default entrenadorEnEquipoService;
